import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "../firebase/config";
import { UploadResult } from "../types/storage";

/**
 * s version types for storage naming
 */
export type PhotoVersion = "preview" | "original" | "final";

/**
 * Upload a photo version to Firebase Storage
 * Supports three versions: preview (watermarked), original (untouched), final (edited)
 *
 * @param file - File to upload
 * @param campusId - Campus identifier
 * @param childId - Child identifier
 * @param photoId - Unique photo identifier (UUID)
 * @param version - Photo version type
 * @param onProgress - Optional callback for progress updates (0-100)
 * @returns Upload result with downloadURL and storage path
 */
export const uploadPhotoVersion = async (
  file: File,
  campusId: string,
  childId: string,
  photoId: string,
  version: PhotoVersion,
  onProgress?: (progress: number) => void
): Promise<UploadResult> => {
  try {
    const timestamp = Date.now();
    const fileExtension = file.name.split(".").pop() || "jpg";
    const fileName = `${photoId}_${version}.${fileExtension}`;
    const storagePath = `portraits/${campusId}/${childId}/${photoId}_${version}.${fileExtension}`;

    console.log(`=== UPLOADING ${version.toUpperCase()} VERSION ===`);
    console.log("Upload details:", {
      fileName,
      storagePath,
      photoId,
      version,
      fileSize: file.size,
      fileType: file.type,
    });

    const storageRef = ref(storage, storagePath);

    // Metadata configuration
    const metadata = {
      contentType: file.type,
      cacheControl: "public, max-age=31536000",
      customMetadata: {
        "photo-id": photoId,
        "photo-version": version,
        "upload-timestamp": timestamp.toString(),
        "original-filename": file.name,
      },
    };

    // Use resumable upload for progress tracking
    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    return new Promise<UploadResult>((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          // Track upload progress
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress?.(progress);
          console.log(`${version} upload progress:`, {
            bytesTransferred: snapshot.bytesTransferred,
            totalBytes: snapshot.totalBytes,
            progress: `${Math.round(progress)}%`,
          });
        },
        (error) => {
          // Handle upload error
          console.error(`=== ${version.toUpperCase()} UPLOAD ERROR ===`);
          console.error(`Error uploading ${version} version:`, error);
          reject(error);
        },
        async () => {
          // Handle successful upload completion
          console.log(`${version} upload completed:`, {
            totalBytes: uploadTask.snapshot.metadata.size,
          });

          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log(`${version} download URL obtained:`, downloadURL);
            console.log(`=== ${version.toUpperCase()} UPLOAD COMPLETE ===`);

            resolve({
              downloadURL,
              storagePath,
              fileName,
              timestamp,
              size: file.size,
              type: file.type,
            });
          } catch (error) {
            console.error(`=== ${version.toUpperCase()} URL FETCH ERROR ===`);
            console.error(`Error getting download URL for ${version}:`, error);
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    console.error(`=== ${version.toUpperCase()} SETUP ERROR ===`);
    console.error(`Error setting up upload for ${version} version:`, error);
    throw error;
  }
};

/**
 * Validate file type and size before upload
 * Allowed types: JPEG, PNG, GIF
 * Max size: 10MB
 *
 * @param file - File to validate
 * @returns true if valid
 * @throws Error if validation fails
 */
export const validateFile = (file: File): boolean => {
  const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
  const maxSize = 10 * 1024 * 1024; // 10MB

  if (!allowedTypes.includes(file.type)) {
    throw new Error(
      "Invalid file type. Only JPEG, PNG, and GIF images are allowed."
    );
  }

  if (file.size > maxSize) {
    throw new Error("File too large. Maximum size is 10MB.");
  }

  return true;
};

/**
 * Helper function to fetch file size from download URL
 * Uses HEAD request to get content-length without downloading
 *
 * @param downloadURL - Firebase Storage download URL
 * @returns File size in bytes, or 0 if unable to fetch
 */
export const fetchFileSize = async (downloadURL: string): Promise<number> => {
  try {
    const response = await fetch(downloadURL, { method: "HEAD" });
    const contentLength = response.headers.get("content-length");
    return contentLength ? parseInt(contentLength, 10) : 0;
  } catch (error) {
    console.warn("HEAD request failed for URL:", downloadURL, error);
    return 0;
  }
};
